import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { components } from "../../api/generated/vision-api";
import { noteMemberships, regionMoveTarget } from "../../servers/discovery";
import { pickOrg, rememberOrg, rememberedOrg, setCurrentOrgId, takeOrgFromAddress } from "./orgStore";

type Membership = components["schemas"]["Membership"];

type OrgState = {
  memberships: Membership[];
  orgId: string | undefined;
  current: Membership | undefined;
  /** Switches to an organisation; one not in the memberships yet (a personal one just made) is switched to once
   * `/v1/me` lists it. */
  switchOrg: (orgId: string) => void;
};

const OrgContext = createContext<OrgState | null>(null);

export function OrgProvider({
  userId,
  memberships,
  children,
}: {
  userId: string;
  memberships: Membership[];
  children: ReactNode;
}) {
  // Each organisation's region, for the API client (WEB-SRV-BOOT-03); before the first request of a child.
  noteMemberships(memberships);
  const [orgId, setOrgId] = useState(() => {
    // An organisation named by the address: the app moved here from another region's origin for it (WEB-SRV-BOOT-04).
    const named = takeOrgFromAddress(memberships);
    if (named) rememberOrg(userId, named);
    const picked = pickOrg(memberships, named ?? rememberedOrg(userId));
    // Set before the first child renders, so the first organisation-scoped request already carries X-Org.
    setCurrentOrgId(picked);
    return picked;
  });
  // `/v1/me` was fetched again and the memberships changed: removed from the selected organisation, or a first one
  // joined in another tab. Pick again while rendering, not in an effect, so no child asks the old organisation and
  // gets 403 until a reload.
  const [pending, setPending] = useState<string>();
  if (pending && memberships.some((m) => m.org_id === pending)) {
    setPending(undefined);
    rememberOrg(userId, pending);
    setCurrentOrgId(pending);
    setOrgId(pending);
  }
  const stillMember = orgId !== undefined && memberships.some((m) => m.org_id === orgId);
  if (!stillMember) {
    const picked = pickOrg(memberships, rememberedOrg(userId));
    if (picked !== orgId) {
      setCurrentOrgId(picked);
      setOrgId(picked);
    }
  }

  const switchOrg = useCallback(
    (id: string) => {
      if (!memberships.some((m) => m.org_id === id)) {
        setPending(id);
        return;
      }
      rememberOrg(userId, id);
      setCurrentOrgId(id);
      setOrgId(id);
    },
    [memberships, userId],
  );

  // The organisation's region serves the web app on another origin: go there, same path (REG-22). Never with one region.
  useEffect(() => {
    const target = regionMoveTarget(orgId);
    if (target) window.location.assign(target);
  }, [orgId]);

  const value = useMemo<OrgState>(
    () => ({ memberships, orgId, current: memberships.find((m) => m.org_id === orgId), switchOrg }),
    [memberships, orgId, switchOrg],
  );
  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg(): OrgState {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrg outside OrgProvider");
  return ctx;
}
